<template>
  <div>
    <v-container fluid pa-0>
      <v-row>
        <!-- Non-subscriber -->
        <template v-if="!subscription">
          <v-col cols="12" d-flex>
            <v-icon
              color="rgba(0,0,0,.3)"
              class="d-inline-flex align-self-center mr-4"
              style="flex: 0 0 !important"
            >
              {{ mdiAlertCircleOutline }}
            </v-icon>
            <div class="align-self-center font-weight-bold">
              You don't have an active subscription with whalewisdom.com
            </div>
          </v-col>
          <v-col cols="12">
            <a href="/pricing" target="_blank" class="font-weight-medium">Subscribe now</a> to
            access the entire 13F database going back as far as the first quarter of 2001!
          </v-col>
        </template>

        <!-- Subscriber -->
        <template v-else>
          <!-- Subscription information -->
          <v-col cols="12">
            <v-card>
              <v-card-title>
                <helpers-section-sub-header>Subscription information</helpers-section-sub-header>
              </v-card-title>
              <v-card-text>
                <!-- Header note -->
                <div class="mb-6 subscription-note-container">
                  <v-icon color="rgba(0,0,0,.3)" class="mr-4">
                    {{ mdiCheckCircle }}
                  </v-icon>
                  <div class="font-weight-bold">
                    You have an active
                    <a href="/pricing" target="_blank">{{ subscription }}</a> subscription with
                    whalewisdom.com
                  </div>
                </div>

                <!-- Payment method used -->
                <div class="mb-6">
                  <!-- Credit card subscriber -->
                  <template
                    v-if="user.recurly_subscriber && billingInfo && billingInfo.billing_info"
                  >
                    <div>
                      <span class="font-weight-medium">{{
                        billingInfo.billing_info.number_current
                      }}</span>
                      is setup as your payment method. Auto-renewal is currently
                      <span class="font-weight-medium">{{
                        isAutoRenewalEnabled ? 'ENABLED' : 'DISABLED'
                      }}</span
                      >.
                    </div>
                    <div class="mt-2">
                      <v-btn
                        :loading="togglingAutoRenewal"
                        :disabled="togglingAutoRenewal"
                        small
                        color="primary"
                        class="mx-0"
                        @click="toggleAutoRenewal()"
                      >
                        {{ isAutoRenewalEnabled ? 'Disable' : 'Enable' }} Auto-renewal
                      </v-btn>
                    </div>
                    <v-alert class="ma-2" color="blue-grey" dark>
                      Your election to discontinue the subscription will take effect after your
                      current subscription expires. There are no refunds once you have subscribed.
                    </v-alert>
                  </template>

                  <!-- Paypal subscriber -->
                  <template v-else-if="user.paypal_subscriber">
                    <div class="font-weight-medium">
                      PayPal is currently used as your payment method.
                    </div>
                    <div>
                      Want to cancel? You may check with
                      <a href="https://paypal.com" target="_blank">PayPal</a> to see if you are
                      setup to automatically renew this subscription.
                    </div>
                  </template>
                </div>

                <!-- Info table -->
                <table class="simple-table simple-table--bordered">
                  <tbody>
                    <tr>
                      <td>Subscription started on:</td>
                      <td v-if="user.user_subscription">
                        {{ dateVerbose(user.user_subscription.subscription_start_date) }}
                      </td>
                      <td v-else-if="user.primary_account">
                        {{
                          dateVerbose(
                            user.primary_account.user_subscription.subscription_start_date
                          )
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td>Subscription ends on:</td>
                      <td v-if="user.user_subscription">
                        {{ dateVerbose(user.user_subscription.subscription_end_date) }}
                      </td>
                      <td v-else-if="user.primary_account">
                        {{
                          dateVerbose(user.primary_account.user_subscription.subscription_end_date)
                        }}
                      </td>
                    </tr>
                    <!-- if Subscription Limits -->
                    <template v-if="user.subscription_limit">
                      <tr>
                        <td>Max allowed filer downloads:</td>
                        <td>{{ user.subscription_limit.number_of_filers }}</td>
                      </tr>
                      <tr>
                        <td>Max allowed stock downloads:</td>
                        <td>{{ user.subscription_limit.number_of_stocks }}</td>
                      </tr>
                      <tr v-if="subscriptionActivity.filer_activity">
                        <td>Filers downloaded during last 90 days:</td>
                        <td>
                          <span v-if="!subscriptionActivity.filer_activity.length"> None </span>
                          <div v-else class="pr-4" style="max-height: 200px; overflow-y: auto">
                            <ul>
                              <li
                                v-for="filer in subscriptionActivity.filer_activity"
                                :key="filer.name"
                              >
                                {{ filer.name }}
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                      <tr v-if="subscriptionActivity.stock_activity">
                        <td>Stocks downloaded last 90 days:</td>
                        <td>
                          <span v-if="!subscriptionActivity.stock_activity.length"> None </span>
                          <div v-else class="pr-4" style="max-height: 200px; overflow-y: auto">
                            <ul>
                              <li
                                v-for="stock in subscriptionActivity.stock_activity"
                                :key="stock.name"
                              >
                                {{ stock.name }}
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Change Subscription Plan -->
          <v-col v-if="user.recurly_subscriber && billingInfo && !planNotFound" cols="12">
            <v-card>
              <v-card-title class="d-block">
                <helpers-section-sub-header class="mb-1">
                  Change subscription plan
                </helpers-section-sub-header>
                <div class="text-grey">Click on a price variant to preview changes</div>
              </v-card-title>
              <v-card-text>
                <v-container fluid pa-0>
                  <v-row>
                    <v-col v-for="plan in plans" :key="plan.name" cols="12" sm="6">
                      <v-hover v-slot="{ isHovering, props }">
                        <v-card
                          v-bind="props"
                          :class="`elevation-${isHovering ? 4 : 0}`"
                          flat
                          tile
                          class="subscription-plan-container"
                        >
                          <!-- Subscription type -->
                          <v-card-title
                            class="text-h5 font-weight-medium bg-primary text-white justify-center"
                          >
                            {{ plan.description }}
                          </v-card-title>

                          <!-- Benefits list -->
                          <v-card-text class="my-1">
                            <v-row>
                              <v-col cols="12">
                                <ul class="benefits-list">
                                  <template
                                    v-for="(benefit, index) in plan.benefits"
                                    :key="`li${index}`"
                                  >
                                    <li class="py-2" v-html="benefit" />
                                    <v-divider v-if="index !== plan.benefits.length - 1" />
                                  </template>
                                </ul>
                              </v-col>
                            </v-row>
                          </v-card-text>

                          <!-- Buttons: Price Variants -->
                          <v-card-actions class="pa-4">
                            <v-row justify="space-between" align="end">
                              <v-col
                                v-for="variant in plan.variants"
                                :key="variant.name"
                                cols="6"
                                pa-1
                              >
                                <div class="variant">
                                  <div
                                    v-if="variant.name === currentPlanCode"
                                    class="font-weight-light"
                                  >
                                    Current plan
                                  </div>
                                  <v-btn variant="outlined" color="primary"
                                    :disabled="variant.name === currentPlanCode"
                                    class="px-2 py-1"
                                    @click="preview(variant.name)"
                                  >
                                    <span class="price  font-weight-medium">
                                      {{ variant.price }}
                                    </span> - 
                                    <span class="text-uppercase">
                                      {{ variant.billed }}
                                    </span>
                                  </v-btn>
                                </div>
                              </v-col>
                            </v-row>
                          </v-card-actions>
                        </v-card>
                      </v-hover>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col v-if="user.recurly_subscriber" cols="12" cols-md="6" cols-lg="4">
            <v-card max-width="475" class="mx-auto">
              <v-list density="compact" style="max-height: 500px; overflow-y: scroll">
                <v-toolbar dark>
                  <v-toolbar-title>Prior Invoices</v-toolbar-title>
                </v-toolbar>

                <v-list-item v-for="invoice in invoices" :key="invoice.id">
                  <v-list-item-title> Invoice {{ invoice.number }} </v-list-item-title>
                  <v-list-item-subtitle
                    >{{ invoice.created_at }} {{ invoice.state }}
                    {{ currency(invoice.total) }}</v-list-item-subtitle
                  >

                  <template #prepend>
                    <v-icon :icon="mdiDownload" @click="downloadInvoice(invoice)"></v-icon>
                  </template>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>
        </template>
      </v-row>
    </v-container>
    <v-dialog v-model="loading" persistent width="300">
      <v-card color="primary" dark class="pa-2">
        <v-card-text>
          Loading subscription information....
          <v-progress-linear indeterminate color="accent" class="mb-0" />
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- Dialog: Preview Plan Change -->
    <v-dialog v-model="previewDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <v-toolbar-title>Invoice</v-toolbar-title>
        </v-card-title>
        <v-card-text>
          <!-- Loading spinner -->
          <v-row v-if="loadingPreviewData" justify="center">
            <v-progress-circular indeterminate color="primary" />
          </v-row>

          <!-- Invoice table -->
          <table
            v-else-if="previewData && !loadingPreviewData"
            class="simple-table simple-table--bordered"
            style="width: 100%"
          >
            <tbody>
              <tr v-for="(lineItem, index) in previewData.line_items" :key="index">
                <td>{{ lineItem.attributes.description }}</td>
                <td>{{ currency(lineItem.attributes.unit_amount) }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">
                  {{ previewData.total >= 0 ? 'Amount Due:' : 'Refund Amount:' }}
                </td>
                <td>{{ currency(previewData.total) }}</td>
              </tr>
            </tbody>
          </table>

          <span v-else>No preview available</span>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-row density="compact">
            <v-col cols="12" sm="6">
              <!-- Cancel -->
              <v-btn :disabled="loadingPreviewData" block @click="previewDialog = false">
                Cancel
              </v-btn>
            </v-col>
            <v-col cols="12" sm="6">
              <!-- Subscribe -->
              <v-btn
                :loading="changingPlan"
                :disabled="changingPlan || loadingPreviewData"
                block
                color="primary"
                @click="changePlan()"
              >
                Change Plan
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import api from '@/store/api'
import { mapStoreState, mapStoreGetters } from '@/store/helpers'

// Mixins
import FormattersMixin from '@/mixins/formatters'
import { mdiAlertCircleOutline, mdiCheckCircle, mdiDownload } from '@mdi/js'

export default {
  mixins: [FormattersMixin],

  data() {
    return {
      mdiAlertCircleOutline,
      mdiCheckCircle,
      mdiDownload,
      loading: false,
      invoices: [],
      loadingInvoices: false,
      previewDialog: false,
      loadingPreviewData: false,
      planNotFound: false,
      togglingAutoRenewal: false,
      changingPlan: false,
      currentPlanCode: '',
      billingInfo: {},
      subscriptionActivity: {},
      previewData: null,
      plans: [
        {
          subscription_type_id: 1,
          description: 'Standard',
          benefits: [
            'No ads',
            'Historical 13F data back to 2001',
            'Historical 13D/G data back to 2006',
            'Export to Excel all 13F data',
            'API access',
            'Up to 10 members per filer group',
            'Download Form D data to Excel (limit 3000 records per download)',
            '<a target="_blank" href="https://whalewisdom.com/info/excel_add_in">Excel Add-in</a>',
            '<a target="_blank" href="https://whalewisdom.com/whaleindex">WhaleIndex</a> and <a target="_blank" href="https://whalewisdom.com/info/whalescores">WhaleScore</a> data',
            'Download limit of 50 stocks and 50 funds every 90 days'
          ],
          variants: [
            {
              name: 'std_quarterly',
              billed: '3 months',
              price: 90
            },
            {
              name: 'std_yearly',
              billed: 'year',
              price: 300
            }
          ]
        },
        {
          subscription_type_id: 2,
          description: 'Pro',
          benefits: [
            '<span class="font-italic font-weight-medium">Everything from Standard plus:</span>',
            'Download limit of 200 stocks/filers historical 13F data every 90 days',
            'Up to 50 members per filer group',
            'Full access to the new Insider Backtester (Form 4 filings)',
            'Advanced backtesting features',
            '<a target="_blank" href="https://whalewisdom.com/whaleindex">Combined holdings report</a>'
          ],
          variants: [
            {
              name: 'pro_quarterly',
              billed: '3 months',
              price: 150
            },
            {
              name: 'pro_yearly',
              billed: 'year',
              price: 500
            }
          ]
        }
      ]
    }
  },

  computed: {
    ...mapStoreState({
      userRoot: state => state.user,
      user: state => state.user.user
    }),
    ...mapStoreGetters('user', ['subscription']),

    isAutoRenewalEnabled() {
      return this.billingInfo && !this.billingInfo.canceled_at
    }
  },

  mounted() {
    this.loadRecurlyScript()
    this.updateSubscriptionInfo()
  },

  methods: {
    toggleAutoRenewal() {
      this.togglingAutoRenewal = true

      api
        .post('user.json', {
          account_action: this.isAutoRenewalEnabled ? 'cancel' : 'reactivate'
        })
        .then(() => this.updateSubscriptionInfo())
        .catch(error => {
          useAppStore().showNotification({
            type: 'error',
            text: (error.response && error.response.data) || error.message
          })
        })
        .then(() => (this.togglingAutoRenewal = false))
    },

    preview(planCode) {
      this.previewDialog = true
      this.loadingPreviewData = true

      api
        .post(`user.json?account_action=preview_plan&plan_code=${planCode}`)
        .then(res => {
          if (res.data) {
            this.previewData = res.data
            this.previewData.planCode = planCode
          }
        })
        .catch(error => {
          useAppStore().showNotification({
            type: 'error',
            text: (error.response && error.response.data) || error.message
          })
        })
        .then(() => (this.loadingPreviewData = false))
    },

    changePlan() {
      this.changingPlan = true

      api
        .post(`user.json?account_action=change_plan&plan_code=${this.previewData.planCode}`)
        .then(res => {
          this.previewDialog = false
          this.currentPlanCode = res.data.plan.plan_code
          useUserStore().loadUser()
          useAppStore().showNotification({ type: 'success', text: 'Plan change complete' })
        })
        .catch(() => {
          useAppStore().showNotification({ type: 'error', text: 'Plan change failed' })
        })
        .then(() => (this.changingPlan = false))
    },

    loadRecurlyScript() {
      const vm = this
      const recurlyScript = document.createElement('script')

      recurlyScript.src = 'https://js.recurly.com/v4/recurly.js'
      recurlyScript.onload = function () {
        vm.$nextTick(function () {
          window.recurly.configure({ publicKey: 'sc-VLk3iuMQaQDqtzjMo6iLMw' })
        })
      }

      document.head.appendChild(recurlyScript)
    },
    downloadInvoice(invoice) {
      window.open(invoice.invoice_url)
    },

    updateSubscriptionInfo() {
      if (this.userRoot.isSubscriber) {
        // Sub limit
        if (this.user.subscription_limit) {
          api
            .get('subscriber_activity')
            .then(response => {
              response.data && (this.subscriptionActivity = response.data)
            })
            .catch(error => {
              useAppStore().showNotification({
                type: 'error',
                text: (error.response && error.response.data) || error.message
              })
            })
        }

        // Recurly sub
        if (this.user.recurly_subscriber) {
          this.loading = true
          api
            .get('billing_info')
            .then(response => {
              if (response.data) {
                if (response.data == 'Not found') {
                  this.planNotFound = true
                } else {
                  this.billingInfo = response.data // .billing_info
                  this.currentPlanCode = response.data.plan_code
                  this.invoices = response.data.invoices

                  if (this.billingInfo.billing_info.billing_type === 'credit_card') {
                    // eslint-disable-next-line
                  this.billingInfo.billing_info.number_current = `${this.billingInfo.billing_info.card_type} ‪•⁠ ⁠•⁠ ⁠•⁠ ⁠• ${this.billingInfo.billing_info.last_four}`
                  }
                }
              }
              this.loading = false
            })
            .catch(error => {
              useAppStore().showNotification({
                type: 'error',
                text: (error.response && error.response.data) || error.message
              })
              this.loading = false
            })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.subscription-note-container {
  display: flex;
  align-items: center;
  > img {
    max-width: 60px;
    max-height: 60px;
  }
}
.subscription-plan-container {
  border: 2px solid $primary;
  user-select: none;
  ul.benefits-list {
    list-style-type: none;
    padding: 0;
    li {
      text-align: center;
    }
  }
  .variant {
    text-align: center;
    &--button {
      height: 100%;
      width: 100%;
      .v-btn__content {
        display: block;
        text-transform: initial;
        .price {
          &:before {
            content: '$';
            font-size: 0.7em;
            vertical-align: top;
            margin-right: 2px;
          }
        }
      }
    }
  }
}
</style>
